import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import {
  ArrowRight, Search, Play, Check, X as XIcon, MapPin, Sparkles,
  Compass, Calendar, BookOpen, Award, DollarSign, Heart, Users,
  Camera, Facebook, Instagram, Twitter, Youtube, Star, MessageCircle,
  ShieldCheck, Zap, Trophy, Gem, Luggage, Home, Umbrella, User,
  Apple, Smartphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { defaultAvatarUrl } from "@/lib/avatar";
import DemoModal from "@/components/DemoModal";
import { useDemoTour } from "@/hooks/useDemoTour";

/**
 * Hero-pill kill-switch. Flip to `true` to re-enable the "Watch Demo"
 * button next to "Start Your Journey". The guided tour code itself
 * (DemoTour + DemoTourProvider + demoTourSteps) is still mounted and
 * ready — this only hides the entry point on the marketing page while
 * we take the storyboard for a second pass.
 */
const DEMO_TOUR_ENABLED = false;

/**
 * Phase 1.1 marketing home — full redesign to match user mockup (Image 2).
 *
 * CRITICAL: uses the REAL brand logo bitmap from `/public/assets/logo-*.png`
 * (owner's actual artwork). Never render an inline JSX/AI wordmark on
 * behalf of the brand — the previous revision made that mistake and had
 * to be reverted.
 */

// Real logo bitmap — same file used by AppShell + AuthPage. Do NOT swap.
const LOGO_LANDSCAPE = "/assets/logo-landscape.png";
const LOGO_SQUARE = "/assets/logo-square.png";

// Cinematic hero slideshow — 8 destinations, cross-fades every 5s.
// Ordered so consecutive frames are visually distinct (mountain → beach
// → city → alps → desert → aurora → jungle → skyline).
const HERO_SLIDES = [
  // 1. Mountain sunset (original hero — anchors the reel)
  "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=2400&q=80",
  // 2. Turquoise tropical beach
  "https://images.unsplash.com/photo-1439066615861-d1af74d74000?auto=format&fit=crop&w=2400&q=80",
  // 3. Santorini blue-white street
  "https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?auto=format&fit=crop&w=2400&q=80",
  // 4. Swiss Alps / alpine lake
  "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=2400&q=80",
  // 5. Desert dunes at golden hour
  "https://images.unsplash.com/photo-1509316785289-025f5b846b35?auto=format&fit=crop&w=2400&q=80",
  // 6. Northern lights over snow
  "https://images.unsplash.com/photo-1483347756197-71ef80e95f73?auto=format&fit=crop&w=2400&q=80",
  // 7. Tropical waterfall / rainforest
  "https://images.unsplash.com/photo-1432405972618-c60b0225b8f9?auto=format&fit=crop&w=2400&q=80",
  // 8. Tokyo neon skyline at night
  "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=2400&q=80",
];
// Legacy single-image constant retained for any downstream references.
const HOME_HERO_IMG = HERO_SLIDES[0];
// Coastal cliffs at golden hour — on-brand vacation scene for the
// bottom "Start writing your next chapter" CTA. v10.7.1 swap: previous
// shot read as a wedding-couple portrait per user feedback.
const CTA_BANNER_IMG =
  "https://images.unsplash.com/photo-1533105079780-92b9be482077?auto=format&fit=crop&w=2400&q=80";
// Community post imagery
const COMMUNITY_IMGS = [
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80", // beach
  "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&w=1200&q=80", // mountain lake
  "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=1200&q=80", // architecture
];

const NAV_LINKS = [
  { href: "#dream-plan", label: "Explore" },
  { href: "#why", label: "Dream" },
  { href: "#difference", label: "Features" },
  { href: "#community", label: "About" },
  { href: "#community", label: "Community" },
];

// v10.4 — When the visitor is logged in, the Home page top-header
// swaps its guest marketing nav for the same 6 app tabs used by the
// SideNav on every other page. This is Home-only; every other page
// still uses the AppShell SideNav.
const LOGGED_IN_TABS = [
  { to: "/",         label: "Home",     Icon: Home },
  { to: "/explore",  label: "Explore",  Icon: Compass },
  { to: "/dream",    label: "Dream",    Icon: Star },
  { to: "/chapters", label: "Chapters", Icon: BookOpen },
  { to: "/passport", label: "Passport", Icon: Award },
  { to: "/lounge",   label: "Lounge",   Icon: Umbrella },
];

const PILLARS = [
  { emoji: "\u2728", label: "Dream", desc: "Save destinations that spark your imagination to create the ultimate dream vacation." },
  { emoji: "\uD83D\uDDFA", label: "Plan", desc: "Turn dreams into itineraries with budgets, dates, and details." },
  { icon: "luggage", label: "Book", desc: "Reserve each trip with curated partners, all in one place \u2014 one journal." },
  { emoji: "\uD83D\uDCD6", label: "Cherish", desc: "Journal every moment. Watch as chapters, stamps, and memories add up." },
];

const WHY_FEATURES = [
  { Icon: Compass, label: "Discover curated stays" },
  { Icon: Calendar, label: "Plan every detail" },
  { Icon: BookOpen, label: "Document your journey" },
  { Icon: Users, label: "Share with friends" },
  { Icon: DollarSign, label: "Track your budget" },
  { Icon: Award, label: "Earn travel rewards" },
  { Icon: Camera, label: "Preserve memories" },
  { Icon: Heart, label: "Remember forever" },
];

const DIFFERENCE = {
  typical: [
    "One-off booking, then forgotten",
    "Scattered notes, tabs, screenshots",
    "No connection between trips",
    "Endless scrolling to find old travel posts",
  ],
  travelbio: [
    "A living journal that grows with you",
    "Every trip organized in one home",
    "Chapters, stamps, memories connect",
    "Your story is yours \u2014 built to remember",
  ],
};

const JOURNEY = [
  { icon: Sparkles, label: "Dream" },
  { icon: Calendar, label: "Book" },
  { icon: MapPin, label: "Vacation" },
  { icon: BookOpen, label: "Journal" },
  { icon: BookOpen, label: "Chapter" },
  { icon: Award, label: "Stamp" },
  { icon: Heart, label: "Memory Saved Forever" },
];

const REWARDS = [
  { Icon: Award, label: "Collect stamps", desc: "One per country, coast, and city." },
  { Icon: Trophy, label: "Complete challenges", desc: "Weekend trips, world tours, hidden gems." },
  { Icon: Zap, label: "Level up", desc: "From Wanderer to World Traveler." },
  { Icon: Gem, label: "Unlock rewards", desc: "Perks, discounts, curator picks." },
];

const COMMUNITY = [
  {
    name: "Ashley R.",
    place: "Lisbon, Portugal",
    hashtags: ["#LisbonSunsets", "#Wanderlust"],
    likes: 128, comments: 14, imgIdx: 2,
  },
  {
    name: "Michael B.",
    place: "Banff, Canada",
    hashtags: ["#MountainMornings", "#CanadaVibes"],
    likes: 96, comments: 8, imgIdx: 1,
  },
  {
    name: "Priya S.",
    place: "Maldives",
    hashtags: ["#IslandLife", "#TravelBio"],
    likes: 214, comments: 22, imgIdx: 0,
  },
];

// 3 testimonial cards — displayed on the home page. Real reviews will
// live here once the community starts submitting them; until then each
// card renders a blurred lorem-style placeholder body (visible only as
// texture) with a clear prompt CTA above it inviting the reader to be
// the first review. The 5-star row is left intact and unblurred so
// the card retains its "review" shape at a glance.
const TESTIMONIALS = [
  {
    heading: "Your travel story could be next.",
    subtext: "Be one of our first travelers to share your experience with the TravelBio community.",
    quote: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum id est vitae massa dictum tincidunt. Curabitur non risus a lacus.",
  },
  {
    heading: "Share your TravelBio story.",
    subtext: "After planning or booking your trip, leave a review and inspire fellow travelers.",
    quote: "Nulla facilisi. Integer ac velit non ipsum tempor congue. Aenean fringilla urna at diam pretium, non maximus arcu porta. Sed at bibendum.",
  },
  {
    heading: "Your adventure belongs here.",
    subtext: "Plan, book, explore, then come back and tell your story.",
    quote: "Maecenas vestibulum vitae elit at vulputate. Praesent efficitur mi vel ligula sagittis, non pulvinar odio scelerisque. Cras eget tempus.",
  },
];

function HomeHeader({ onLogin, onSignUp }) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const avatar = user?.avatar_url || (user ? defaultAvatarUrl(user?.email || "") : null);

  return (
    <header
      data-testid="home-header"
      className="sticky top-0 z-40 bg-tb-navy-deep/95 backdrop-blur-md border-b border-tb-gold/25"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 py-3">
        {/* Left slot intentionally empty on the home page — the real
            wordmark is centered in the hero below, so a second copy up
            here reads as redundant. Header stays as an anchor for the
            right-side auth buttons and (later) the nav links. */}
        <div className="flex items-center gap-2" aria-hidden="true" />

        {/* Center nav — v10.4 splits by auth state.
              - Guest (marketing): keep the current anchor-nav
                (Explore/Dream/Features/About/Community) exactly.
              - Logged-in: swap in the SAME 6 app tabs the SideNav
                shows on every other page, using SPA <NavLink>s with a
                subtle gold underline on the active route. */}
        <nav className="hidden lg:flex items-center gap-7">
          {user ? (
            LOGGED_IN_TABS.map((t) => {
              // Home tab points to "/" per spec, but the app's
              // Redirector sends "/" → "/home" for logged-in users, so
              // the actual pathname on the Home page is "/home". Treat
              // both as "on Home".
              const isActive = t.to === "/"
                ? pathname === "/" || pathname === "/home"
                : pathname === t.to;
              return (
                <NavLink
                  key={t.label}
                  to={t.to}
                  end={t.to === "/"}
                  data-testid={`home-nav-app-${t.label.toLowerCase()}`}
                  className={
                    "font-hand text-sm transition pb-1 " +
                    (isActive
                      ? "text-tb-gold-bright border-b-2 border-tb-gold"
                      : "text-tb-cream/85 hover:text-tb-gold-bright border-b-2 border-transparent")
                  }
                >
                  {t.label}
                </NavLink>
              );
            })
          ) : (
            NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`home-nav-${l.label.toLowerCase()}`}
                className="font-hand text-sm text-tb-cream/85 hover:text-tb-gold-bright transition"
              >
                {l.label}
              </a>
            ))
          )}
        </nav>

        {/* Right slot — v10.4 restyled.
              - Guest: single gold "Login / Sign Up" pill (unchanged).
              - Logged-in: circular avatar with gold border → /profile.
                Uses `user.avatar_url` when present, otherwise a
                default gravatar-style URL, otherwise the User icon in
                a navy circle. */}
        <div className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <button
              data-testid="home-profile-btn"
              onClick={() => nav("/profile")}
              className="w-10 h-10 rounded-full border-2 border-tb-gold bg-tb-navy flex items-center justify-center overflow-hidden hover:brightness-110 transition tb-tap"
              aria-label="Your profile"
              title="Your profile"
            >
              {avatar ? (
                <img src={avatar} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-tb-gold-bright" strokeWidth={1.8} />
              )}
            </button>
          ) : (
            <button
              data-testid="home-auth-btn"
              onClick={onLogin}
              className="rounded-full bg-tb-gold text-tb-navy-deep px-5 py-1.5 font-hand font-semibold text-sm hover:brightness-105 transition tb-tap border border-tb-gold"
            >
              Login / Sign Up
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero({ onStart, onDemo }) {
  // Slideshow state — cross-fades through HERO_SLIDES every 5s. Real
  // logo, caption, tagline, and buttons sit above the image stack and
  // stay pinned so only the background flexes. Preloads all frames on
  // mount so no cross-fade ever flashes an unloaded image. Respects
  // `prefers-reduced-motion` (holds on frame 0 permanently).
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    // Preload every slide so the first cross-fade is instant.
    HERO_SLIDES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });

    // Motion opt-out: honor the user's system pref and skip the cycle.
    const mql =
      typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : null;
    if (mql && mql.matches) return undefined;

    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % HERO_SLIDES.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      data-testid="home-hero"
      className="relative w-full overflow-hidden"
      style={{ minHeight: "82vh" }}
    >
      {/* Cross-fading image stack — all 8 slides mounted concurrently,
          only the active one has opacity 1. `transition-opacity` with
          a 1.5s duration + ease-in-out gives a proper cinematic fade. */}
      {HERO_SLIDES.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          aria-hidden="true"
          data-testid={`home-hero-slide-${i}`}
          className="absolute inset-0 w-full h-full object-cover transition-opacity"
          style={{
            transitionDuration: "1500ms",
            transitionTimingFunction: "ease-in-out",
            opacity: i === activeIdx ? 1 : 0,
          }}
          draggable={false}
        />
      ))}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(20,44,74,0.28) 0%, rgba(20,44,74,0.5) 60%, rgba(20,44,74,0.85) 100%)",
        }}
      />
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 pt-24 pb-24 min-h-[82vh]">
        {/* REAL brand bitmap centered large */}
        <img
          data-testid="home-hero-logo"
          src={LOGO_LANDSCAPE}
          alt="TravelBio"
          className="w-full max-w-lg sm:max-w-xl mb-6 drop-shadow-lg"
          style={{ height: "auto", objectFit: "contain" }}
          draggable={false}
        />
        <p
          className="font-hand text-tb-gold-bright text-xs sm:text-sm tracking-[0.35em] uppercase mb-8"
          data-testid="home-hero-caption"
        >
          Your Lifelong Travel Journal
        </p>
        <h1
          data-testid="home-hero-tagline"
          className="font-serif-it italic text-white leading-[1.05] drop-shadow-lg max-w-4xl"
          style={{ fontSize: "clamp(2.25rem, 5.4vw, 4rem)", textWrap: "balance" }}
        >
          Dream it. Plan it. Book it. Cherish it.
        </h1>
        <p className="mt-6 font-hand text-white/90 text-base sm:text-lg leading-relaxed max-w-2xl">
          Welcome to your all-in-one travel booking platform. TravelBio offers
          a unique experience where every journey becomes part of your own
          travel biography. Explore destinations, build your dream vacation,
          book any type of travel, and preserve your memories in one place.
          With options to book on your own or with one of our trusted
          TravelBio Advisors, you'll watch your adventures connect one by one,
          year by year. Join for free to discover just how much more awaits
          inside.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row items-center gap-3">
          <button
            data-testid="home-start-btn"
            onClick={onStart}
            className="inline-flex items-center gap-2 rounded-full bg-tb-gold text-tb-navy-deep px-8 py-4 font-hand font-semibold text-lg hover:brightness-105 hover:shadow-[0_10px_40px_-10px_rgba(230,184,74,0.65)] transition tb-tap"
            style={{ minHeight: 56 }}
          >
            Start Your Journey
            <ArrowRight className="w-5 h-5" />
          </button>
          {/* Watch Demo pill temporarily hidden per product request —
              tour is still on the shelf and will be re-enabled once the
              storyboard gets a second pass. All the underlying code
              (DemoTour + DemoTourProvider + demoTourSteps) is still
              mounted so flipping DEMO_TOUR_ENABLED back to true here is
              a one-line re-enable. */}
          {DEMO_TOUR_ENABLED && (
            <button
              data-testid="home-demo-btn"
              onClick={onDemo}
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/80 bg-white/5 backdrop-blur-sm text-white px-7 py-4 font-hand font-semibold text-lg hover:bg-white/15 transition tb-tap"
              style={{ minHeight: 56 }}
            >
              <Play className="w-4 h-4" fill="currentColor" />
              Watch Demo
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function AdvisorSection({ onFind, onHowItWorks }) {
  return (
    <section
      data-testid="home-advisor-section"
      className="w-full bg-white text-tb-navy px-6 py-16 sm:py-20 border-b border-tb-cream-3"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
        {/* Left column — advisor lifestyle image */}
        <div className="relative rounded-2xl overflow-hidden shadow-md order-2 md:order-1">
          <img
            data-testid="home-advisor-image"
            src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80"
            alt="A TravelBio Advisor working with a client on a travel plan"
            className="w-full h-64 sm:h-80 md:h-[420px] object-cover"
            draggable={false}
          />
          {/* TODO: replace with a real TravelBio Advisor lifestyle photo
              once the marketing shoot lands. Currently using a warm
              Unsplash stock shot of two people planning together. */}
        </div>

        {/* Right column — copy + CTAs */}
        <div className="order-1 md:order-2">
          <p
            data-testid="home-advisor-eyebrow"
            className="font-hand text-tb-gold-bright text-xs sm:text-sm tracking-[0.35em] uppercase mb-4"
          >
            TravelBio Advisors
          </p>
          <h2
            data-testid="home-advisor-headline"
            className="text-tb-navy leading-[1.1] max-w-xl"
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: "clamp(1.9rem, 3.4vw, 3rem)",
              textWrap: "balance",
            }}
          >
            Prefer to book with a human?
          </h2>
          <p
            data-testid="home-advisor-copy"
            className="mt-5 font-hand text-tb-navy/80 text-base sm:text-lg leading-relaxed max-w-xl"
          >
            TravelBio Advisors are a growing community of passionate travel
            professionals dedicated to helping you plan unforgettable
            journeys, at no additional cost to you! Whether you're dreaming
            of a honeymoon, a family reunion at sea, or a once-in-a-lifetime
            adventure, your advisor can provide personalized recommendations,
            help you compare your options, and handle the planning details so
            you can focus on making memories.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              data-testid="home-advisor-find-btn"
              onClick={onFind}
              type="button"
              className="inline-flex items-center gap-2 rounded-full bg-tb-gold text-tb-navy-deep px-7 py-3.5 font-hand font-semibold text-base hover:brightness-105 hover:shadow-[0_10px_40px_-10px_rgba(230,184,74,0.65)] transition tb-tap"
              style={{ minHeight: 52 }}
            >
              Find an Advisor
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="home-advisor-how-link"
              onClick={onHowItWorks}
              type="button"
              className="font-hand text-tb-navy text-sm sm:text-base underline underline-offset-4 decoration-tb-navy/40 hover:decoration-tb-navy transition tb-tap"
            >
              How TravelBio Advisors work
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}


function PillarsRow() {
  return (
    <section
      data-testid="home-pillars"
      className="w-full bg-tb-cream text-tb-navy px-6 py-16 sm:py-20 border-y border-tb-cream-3"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-6 text-center">
        {PILLARS.map((p, i) => (
          <div key={p.label} data-testid={`home-pillar-${i}`} className="flex flex-col items-center">
            <div
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-tb-gold/15 border-2 border-tb-gold/40 flex items-center justify-center mb-4"
              style={{ fontSize: 34 }}
              aria-hidden="true"
            >
              {/* Book pillar → real suitcase icon (was painter's palette
                  emoji, which read as "art" not "travel"). Everything
                  else keeps the warm emoji glyph. */}
              {p.icon === "luggage"
                ? <Luggage className="w-8 h-8 text-tb-navy" strokeWidth={1.8} />
                : p.emoji}
            </div>
            <h3 className="font-serif-it text-tb-navy text-xl sm:text-2xl mb-1">{p.label}</h3>
            <p className="font-ui text-tb-navy/70 text-sm leading-relaxed max-w-[220px]">{p.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhySection() {
  return (
    <section
      id="why"
      data-testid="home-why"
      className="w-full bg-tb-navy-deep px-6 py-20 sm:py-28"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <p className="font-hand uppercase tracking-[0.25em] text-tb-gold/80 text-xs mb-3">
            Why TravelBio
          </p>
          <h2
            className="font-serif-it italic text-tb-cream leading-tight mb-4"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            Everything you love about travel — in one place.
          </h2>
          <p className="font-ui text-tb-cream/75 text-base sm:text-lg leading-relaxed mb-8">
            From the flutter of a first idea to the pages of a completed journal,
            TravelBio keeps every part of the journey together.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {WHY_FEATURES.map(({ Icon, label }, i) => (
              <li
                key={label}
                data-testid={`home-why-feature-${i}`}
                className="flex items-center gap-3"
              >
                <span className="w-8 h-8 rounded-full bg-tb-gold/15 border border-tb-gold/45 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-tb-gold-bright" strokeWidth={1.8} />
                </span>
                <span className="font-hand text-tb-cream/90 text-sm sm:text-base">{label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Phone mockup — real logo hero + fake app screen */}
        <div className="flex justify-center lg:justify-end">
          <div
            data-testid="home-why-phone-mockup"
            className="relative rounded-[2.5rem] bg-tb-navy border-[10px] border-tb-navy shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden"
            style={{ width: 280, height: 560 }}
          >
            {/* Notch */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-tb-navy rounded-b-2xl z-10" />
            {/* Screen */}
            <div className="w-full h-full bg-tb-cream flex flex-col">
              <div className="bg-tb-navy-deep h-16 flex items-center justify-center border-b border-tb-gold/40">
                <img src={LOGO_LANDSCAPE} alt="" style={{ height: 32 }} draggable={false} />
              </div>
              <div className="flex-1 overflow-hidden">
                <div
                  style={{
                    backgroundImage: `url(${COMMUNITY_IMGS[1]})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    height: "45%",
                  }}
                />
                <div className="p-4">
                  <p className="font-hand text-[10px] uppercase tracking-widest text-tb-gold-soft mb-1">
                    Discover
                  </p>
                  <h4 className="font-serif-it text-tb-navy text-lg leading-tight">
                    Where to next?
                  </h4>
                  <p className="font-ui text-tb-navy/70 text-xs mt-2 leading-relaxed">
                    Curated destinations, saved for the moment inspiration strikes.
                  </p>
                  <div className="mt-3 flex items-center gap-1.5">
                    {["Beach", "Mountain", "City"].map((c) => (
                      <span key={c} className="px-2 py-1 rounded-full bg-tb-navy/10 border border-tb-navy/15 font-hand text-[10px] text-tb-navy/80">
                        {c}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 rounded-lg bg-tb-navy text-tb-gold-bright font-hand text-xs text-center py-2 border border-tb-gold/60">
                    Start Exploring
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DifferenceSection() {
  return (
    <section
      id="difference"
      data-testid="home-difference"
      className="w-full bg-tb-cream px-6 py-20 sm:py-28"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="font-hand uppercase tracking-[0.25em] text-tb-navy/60 text-xs mb-3">
            The Difference
          </p>
          <h2
            className="font-serif-it italic text-tb-navy leading-tight"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            The TravelBio Difference.
          </h2>
          <p className="mt-4 font-ui text-tb-navy/70 text-base sm:text-lg max-w-2xl mx-auto">
            Most travel apps forget you the second the trip's over. We were built
            to remember.
          </p>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {/* VS badge */}
          <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-tb-gold text-tb-navy-deep font-serif italic font-bold text-lg items-center justify-center border-4 border-tb-cream shadow-lg">
            vs
          </div>

          <div
            data-testid="home-difference-typical"
            className="rounded-2xl bg-white border-2 border-tb-cream-3 p-6 sm:p-8"
          >
            <p className="font-hand uppercase tracking-widest text-tb-navy/50 text-xs mb-3">
              Typical Travel Experience
            </p>
            <h3 className="font-serif-it text-tb-navy text-2xl mb-5">The forgetful kind.</h3>
            <ul className="space-y-3">
              {DIFFERENCE.typical.map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-tb-coral/15 border border-tb-coral/40 flex items-center justify-center shrink-0 mt-0.5">
                    <XIcon className="w-3.5 h-3.5 text-tb-coral" strokeWidth={2.5} />
                  </span>
                  <span className="font-ui text-tb-navy/70 text-sm sm:text-base">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            data-testid="home-difference-travelbio"
            className="rounded-2xl bg-tb-navy text-tb-cream border-2 border-tb-gold/60 p-6 sm:p-8 shadow-[0_18px_36px_-24px_rgba(0,0,0,0.55)]"
          >
            <p className="font-hand uppercase tracking-widest text-tb-gold/85 text-xs mb-3">
              TravelBio
            </p>
            <h3 className="font-serif-it italic text-tb-cream text-2xl mb-5">The kind that stays.</h3>
            <ul className="space-y-3">
              {DIFFERENCE.travelbio.map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-tb-gold/25 border border-tb-gold flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-tb-gold-bright" strokeWidth={2.5} />
                  </span>
                  <span className="font-ui text-tb-cream/90 text-sm sm:text-base">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function JourneySection() {
  return (
    <section
      data-testid="home-journey"
      className="w-full bg-tb-navy-deep px-6 py-20 sm:py-28"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="font-hand uppercase tracking-[0.25em] text-tb-gold/80 text-xs mb-3">
            Your Journey
          </p>
          <h2
            className="font-serif-it italic text-tb-cream leading-tight"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            Your Travel Journey.
          </h2>
          <p className="mt-4 font-ui text-tb-cream/70 text-base sm:text-lg max-w-2xl mx-auto">
            Seven steps from spark to permanent memory.
          </p>
        </div>

        <div className="relative">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-8 left-8 right-8 h-px bg-tb-gold/30" />
          <ol className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-y-8 gap-x-4">
            {JOURNEY.map((step, i) => {
              const Icon = step.icon;
              return (
                <li
                  key={step.label}
                  data-testid={`home-journey-${i}`}
                  className="flex flex-col items-center text-center relative z-10"
                >
                  <div className="w-16 h-16 rounded-full bg-tb-navy border-2 border-tb-gold flex items-center justify-center mb-3 shadow-[0_0_0_6px_rgba(230,184,74,0.08)]">
                    <Icon className="w-6 h-6 text-tb-gold-bright" strokeWidth={1.7} />
                  </div>
                  <p className="font-hand text-tb-cream text-xs sm:text-sm leading-tight">
                    {step.label}
                  </p>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}

function RewardsSection() {
  return (
    <section
      data-testid="home-rewards"
      className="w-full bg-tb-cream px-6 py-20 sm:py-28"
    >
      <div className="max-w-5xl mx-auto rounded-3xl bg-tb-navy-deep border-2 border-tb-gold/40 p-8 sm:p-12 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]">
        <div className="text-center mb-10">
          <p className="font-hand uppercase tracking-[0.25em] text-tb-gold-bright text-xs mb-3">
            Rewards
          </p>
          <h2
            className="font-serif-it italic text-tb-cream leading-tight"
            style={{ fontSize: "clamp(1.75rem, 3.6vw, 2.75rem)" }}
          >
            Earn Rewards for Every Adventure.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
          {REWARDS.map(({ Icon, label, desc }, i) => (
            <div
              key={label}
              data-testid={`home-reward-${i}`}
              className="flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-tb-gold text-tb-navy-deep border-4 border-tb-gold-soft flex items-center justify-center mb-4 shadow-[0_0_30px_rgba(245,198,74,0.35)]">
                <Icon className="w-7 h-7 sm:w-8 sm:h-8" strokeWidth={1.8} />
              </div>
              <h4 className="font-serif-it text-tb-cream text-lg sm:text-xl mb-1">{label}</h4>
              <p className="font-ui text-tb-cream/65 text-xs sm:text-sm leading-relaxed max-w-[180px]">
                {desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CommunityAndTestimonials() {
  return (
    <section
      id="community"
      data-testid="home-community-section"
      className="w-full bg-tb-navy-deep px-6 py-20 sm:py-28"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-12">
        {/* Community posts */}
        <div>
          <p className="font-hand uppercase tracking-[0.25em] text-tb-gold/80 text-xs mb-3">
            Community
          </p>
          <h2
            className="font-serif-it italic text-tb-cream leading-tight mb-8"
            style={{ fontSize: "clamp(1.75rem, 3.6vw, 2.75rem)" }}
          >
            From Our Community.
          </h2>
          <div className="space-y-5">
            {COMMUNITY.map((p, i) => (
              <article
                key={p.name}
                data-testid={`home-community-${i}`}
                className="relative rounded-2xl bg-tb-cream text-tb-navy border border-tb-cream-3 overflow-hidden shadow-[0_18px_36px_-24px_rgba(0,0,0,0.55)]"
              >
                {/* 2026-07-18 — Blur ALL card content (photo, name,
                    hashtags, hearts, comment count, View Lounge)
                    so the card reads as "your story could be here"
                    instead of a dead placeholder. Real users' posts
                    will replace this once the community populates. */}
                <div
                  aria-hidden="true"
                  className="select-none pointer-events-none"
                  style={{ filter: "blur(7px)" }}
                >
                  <div className="p-4 sm:p-5 flex items-center gap-3 border-b border-tb-cream-3">
                    <div className="w-10 h-10 rounded-full bg-tb-gold/20 border-2 border-tb-gold flex items-center justify-center font-serif-it italic text-tb-navy font-bold">
                      {p.name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-hand font-semibold text-tb-navy text-sm">{p.name}</p>
                      <p className="font-hand text-tb-navy/55 text-xs flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {p.place}
                      </p>
                    </div>
                  </div>
                  <img
                    src={COMMUNITY_IMGS[p.imgIdx]}
                    alt=""
                    className="w-full aspect-[16/9] object-cover"
                    draggable={false}
                  />
                  <div className="p-4 sm:p-5">
                    <p className="font-hand text-tb-navy/70 text-sm">
                      {p.hashtags.join(" \u00b7 ")}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-4 font-hand text-tb-navy/65 text-xs">
                        <span className="inline-flex items-center gap-1">
                          <Heart className="w-3.5 h-3.5" /> {p.likes}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <MessageCircle className="w-3.5 h-3.5" /> {p.comments}
                        </span>
                      </div>
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-tb-navy text-tb-gold-bright border border-tb-gold px-3 py-1.5 font-hand text-xs"
                      >
                        View Lounge <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center px-4">
                  <span
                    data-testid={`home-community-placeholder-${i}`}
                    className="inline-block rounded-full bg-white border border-tb-navy/20 px-4 py-2 font-serif-it italic text-tb-navy text-sm sm:text-base leading-snug shadow-[0_6px_18px_-6px_rgba(0,0,0,0.25)] text-center"
                  >
                    Your travel story could be shared here!
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Testimonials sidebar */}
        <aside>
          <p className="font-hand uppercase tracking-[0.25em] text-tb-gold/80 text-xs mb-3">
            Reviews
          </p>
          <h2
            className="font-serif-it italic text-tb-cream leading-tight mb-8"
            style={{ fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}
          >
            What Travelers Are Saying.
          </h2>
          <div className="space-y-4">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={i}
                data-testid={`home-testimonial-${i}`}
                className="relative rounded-2xl bg-tb-cream text-tb-navy p-5 border border-tb-cream-3 overflow-hidden"
              >
                <div className="flex items-center gap-1 mb-2" aria-label="5 out of 5">
                  {[0, 1, 2, 3, 4].map((k) => (
                    <Star key={k} className="w-3.5 h-3.5 text-tb-gold" fill="currentColor" strokeWidth={0} />
                  ))}
                </div>

                {/* Prompt: bold heading + italic subtext. Sits ABOVE the
                    blurred body so the card reads as "your review goes
                    here" instead of a dead placeholder. */}
                <p
                  data-testid={`home-testimonial-heading-${i}`}
                  className="font-serif text-tb-navy font-bold text-sm sm:text-base leading-snug"
                >
                  {t.heading}
                </p>
                <p
                  data-testid={`home-testimonial-subtext-${i}`}
                  className="mt-1 font-hand italic text-tb-navy/70 text-xs sm:text-sm leading-snug"
                >
                  {t.subtext}
                </p>

                {/* Blurred lorem body — texture only. Hidden from
                    assistive tech (real content lives in the prompt
                    above), and unselectable so users can't dodge the
                    blur by copying the text. */}
                <blockquote
                  data-testid={`home-testimonial-blur-${i}`}
                  aria-hidden="true"
                  className="mt-3 font-serif-it italic text-tb-navy/60 text-sm sm:text-base leading-relaxed select-none pointer-events-none"
                  style={{ filter: "blur(4.5px)" }}
                >
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption
                  aria-hidden="true"
                  className="mt-3 font-hand text-xs text-tb-navy/50 italic"
                >
                  Future traveler <span className="text-tb-navy/35">{"\u00B7 TravelBio"}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}

function BottomCTA({ onCreate }) {
  return (
    <section
      data-testid="home-bottom-cta"
      className="relative w-full overflow-hidden"
      style={{ minHeight: 380 }}
    >
      <img
        src={CTA_BANNER_IMG}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(20,44,74,0.55) 0%, rgba(20,44,74,0.8) 100%)",
        }}
      />
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-20 sm:py-24">
        <h2
          className="font-serif-it italic text-white leading-tight max-w-3xl drop-shadow-lg"
          style={{ fontSize: "clamp(2rem, 4.4vw, 3.25rem)" }}
        >
          Start writing your next chapter.
        </h2>
        <p className="mt-4 font-ui text-white/85 text-base sm:text-lg max-w-xl">
          Free to join. Free to dream. Yours forever.
        </p>
        <button
          data-testid="home-cta-create-account"
          onClick={onCreate}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-tb-gold text-tb-navy-deep px-8 py-4 font-hand font-semibold text-lg hover:brightness-105 tb-tap"
          style={{ minHeight: 56 }}
        >
          Create Free Account
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
}

function HomeFooter() {
  // 2026-07-18 — Android app is not yet available on Play Store.
  // Inline "Coming soon!" reveal (no toast — Toaster is unmounted).
  const [androidHint, setAndroidHint] = useState(false);
  const androidTimer = useRef(null);
  const showAndroidHint = () => {
    setAndroidHint(true);
    if (androidTimer.current) clearTimeout(androidTimer.current);
    androidTimer.current = setTimeout(() => setAndroidHint(false), 2000);
  };
  useEffect(
    () => () => { if (androidTimer.current) clearTimeout(androidTimer.current); },
    [],
  );

  const COLS = [
    {
      heading: "Company",
      links: [
        { to: "/about", label: "About Us" },
        { to: "/contact", label: "Contact" },
        { to: "/lounge", label: "Community" },
      ],
    },
    {
      heading: "Support",
      links: [
        { to: "/contact", label: "Help Center" },
        { to: "/contact", label: "Report an Issue" },
      ],
    },
    {
      heading: "Legal",
      links: [
        { to: "/policies", label: "Policies" },
        { to: "/policies/terms", label: "Terms" },
        { to: "/policies/privacy", label: "Privacy" },
      ],
    },
  ];

  return (
    <footer
      data-testid="home-footer"
      className="w-full bg-tb-navy-deep border-t border-tb-gold/25 text-tb-cream/80 px-6 py-14"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2 md:col-span-1">
          {/* Enlarged footer logo — was ~40px inline with the tagline.
              Now it takes the visual lead (~80px), tagline drops below
              in smaller muted cream for a proper brand-mark hierarchy. */}
          <img
            src={LOGO_LANDSCAPE}
            alt="TravelBio"
            style={{ height: 80, width: "auto", objectFit: "contain" }}
            draggable={false}
          />
          <p className="mt-3 font-serif-it italic text-tb-cream/55 text-xs">
            your lifelong travel journal
          </p>
        </div>
        {COLS.map((col) => (
          <div key={col.heading}>
            <p className="font-hand uppercase tracking-widest text-tb-gold-bright text-xs mb-3">
              {col.heading}
            </p>
            <ul className="space-y-2">
              {col.links.map((l) => (
                <li key={`${col.heading}-${l.label}`}>
                  <NavLink
                    to={l.to}
                    className="font-hand text-sm text-tb-cream/75 hover:text-tb-gold-bright transition"
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <div>
          <p className="font-hand uppercase tracking-widest text-tb-gold-bright text-xs mb-3">
            Connect
          </p>
          <div className="flex items-center gap-3">
            <a
              href="https://www.facebook.com/share/19EXBYMghY/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="footer-connect-facebook"
              className="w-9 h-9 rounded-full border border-tb-gold/40 flex items-center justify-center text-tb-gold hover:bg-tb-gold/15 transition tb-tap"
            >
              <Facebook className="w-4 h-4" strokeWidth={1.6} />
            </a>
          </div>
        </div>
        {/* Download Apps — 2026-07-18. iOS links to App Store; Android
            shows an inline "Coming soon!" until the Play Store build
            is submitted. Sits next to Connect on md+. */}
        <div>
          <p className="font-hand uppercase tracking-widest text-tb-gold-bright text-xs mb-3">
            Download Apps
          </p>
          <div className="flex items-center gap-3">
            <a
              href="https://apps.apple.com/app/id6786411741"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on the App Store"
              data-testid="footer-download-ios"
              className="inline-flex flex-col items-center gap-1 group tb-tap"
            >
              <span className="w-9 h-9 rounded-full border border-tb-gold/40 flex items-center justify-center text-tb-gold group-hover:bg-tb-gold/15 transition">
                <Apple className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <span className="font-hand text-[11px] text-tb-cream/70 group-hover:text-tb-gold-bright transition">
                iOS
              </span>
            </a>
            <div className="relative">
              <button
                type="button"
                onClick={showAndroidHint}
                aria-label="Android app coming soon"
                data-testid="footer-download-android"
                className="inline-flex flex-col items-center gap-1 group tb-tap"
              >
                <span className="w-9 h-9 rounded-full border border-tb-gold/40 flex items-center justify-center text-tb-gold group-hover:bg-tb-gold/15 transition">
                  <Smartphone className="w-4 h-4" strokeWidth={1.6} />
                </span>
                <span className="font-hand text-[11px] text-tb-cream/70 group-hover:text-tb-gold-bright transition">
                  Android
                </span>
              </button>
              {androidHint && (
                <span
                  role="status"
                  aria-live="polite"
                  data-testid="footer-android-coming-soon"
                  className="absolute left-1/2 -translate-x-1/2 -top-9 whitespace-nowrap rounded-full bg-white border border-tb-navy/20 px-3 py-1 font-serif-it italic text-tb-navy text-xs shadow-[0_6px_18px_-6px_rgba(0,0,0,0.35)] animate-in fade-in zoom-in-95"
                >
                  Coming soon!
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-tb-gold/15 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="font-hand text-xs text-tb-cream/50">© 2026 MyTravelBio LLC</p>
        <p className="font-hand text-xs text-tb-cream/50 flex items-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5" /> Private by design
        </p>
      </div>
    </footer>
  );
}

export default function HomePage() {
  const nav = useNavigate();
  const tour = useDemoTour();

  // Both header buttons route to /auth (user preference: no distinct
  // signup form — the auth page toggles login/register internally).
  const goLogin = useCallback(() => nav("/auth"), [nav]);
  const goSignUp = useCallback(() => nav("/auth"), [nav]);
  const startJourney = useCallback(() => nav("/explore"), [nav]);
  // Item — Watch Demo now launches the guided product tour that
  // walks the user through Explore → Dream → Chapters → Passport →
  // Lounge with spotlights on the real tabs. See DemoTour.jsx.
  const openDemo = useCallback(() => tour.start("/explore"), [tour]);

  return (
    <div
      data-testid="home-page"
      className="min-h-screen w-full bg-tb-navy-deep text-tb-cream flex flex-col"
      data-tour="home-hero"
    >
      <HomeHeader onLogin={goLogin} onSignUp={goSignUp} />
      <Hero onStart={startJourney} onDemo={openDemo} />
      <AdvisorSection
        onFind={() => nav("/advisors")}
        onHowItWorks={() => nav("/advisors/how-it-works")}
      />
      <PillarsRow />
      <WhySection />
      <DifferenceSection />
      <JourneySection />
      <RewardsSection />
      <CommunityAndTestimonials />
      <BottomCTA onCreate={goSignUp} />
      <HomeFooter />
    </div>
  );
}
